import {
  useState,
  useEffect,
  useCallback,
  memo,
  useMemo,
  useRef,
} from "react";

import Select, {
  MultiValue,
  StylesConfig,
  components,
  InputActionMeta,
  MenuProps,
  MenuListProps,
  OptionProps,
  MultiValueProps,
} from "react-select";

import { Option } from "../../interface/Option";
import { MedicalCodesAxiosInstance } from "../../Network/MedicalCodesAxiosInstance";

interface ListofIcdMedicalCodeProps {
  onChange: (selected: Option[]) => void;
  clear?: boolean;
  defaultValue?: Option[];

  /**
   * Kept only for backward compatibility with existing callers.
   * The ICD search now uses the typed search value as the backend path parameter.
   */
  description?: string;

  qaIdPrefix?: string;
}

const DEFAULT_QA_PREFIX = "list-of-icd-medical-code";
const MIN_SEARCH_LENGTH = 3;
const RESULT_LIMIT = 10;

const qaSafe = (
  value: string | number | undefined | null
) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildQaId = (
  prefix: string,
  suffix: string | number
) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;

const qaAttrs = (
  prefix: string,
  suffix: string | number
) => {
  const id = buildQaId(prefix, suffix);

  return {
    id,
    "data-testid": id,
    "data-qa": id,
  };
};

const ListofIcdMedicalCode = memo(
  ({
    onChange,
    clear,
    defaultValue,

    // Kept in the prop contract so existing pages do not break.
    description: _description,

    qaIdPrefix = DEFAULT_QA_PREFIX,
  }: ListofIcdMedicalCodeProps) => {
    const [options, setOptions] = useState<Option[]>([]);

    const [selectedOptions, setSelectedOptions] =
      useState<Option[]>(defaultValue || []);

    const [loading, setLoading] = useState(false);

    const [inputValue, setInputValue] =
      useState<string>("");

    const cache = useMemo(
      () => new Map<string, Option[]>(),
      []
    );

    const requestSequence = useRef(0);

    const selectQaId = buildQaId(
      qaIdPrefix,
      "select"
    );

    const inputQaId = buildQaId(
      qaIdPrefix,
      "input"
    );

    const loadOptions = useCallback(async () => {
      const searchTerm = inputValue.trim();

      if (
        searchTerm.length <
        MIN_SEARCH_LENGTH
      ) {
        setOptions([]);
        setLoading(false);

        return;
      }

      const cacheKey =
        searchTerm.toLowerCase();

      if (cache.has(cacheKey)) {
        setOptions(
          cache.get(cacheKey)!
        );

        return;
      }

      const requestId =
        ++requestSequence.current;

      setLoading(true);

      try {
        /**
         * Backend search:
         *
         * GET /getIcd?q=...
         *
         * Search starts after 3 characters.
         */
        const response =
          await MedicalCodesAxiosInstance.get(
            "getIcd",
            {
              params: {
                q: searchTerm,
                limit: RESULT_LIMIT,
              },
            }
          );

        /*
         * Ignore an old request if a
         * newer search already started.
         */
        if (
          requestId !==
          requestSequence.current
        ) {
          return;
        }

        const raw =
          response.data?.result ??
          response.data?.data ??
          response.data ??
          [];

        const data =
          Array.isArray(raw)
            ? raw
            : Array.isArray(
                raw?.result
              )
            ? raw.result
            : Array.isArray(
                raw?.data
              )
            ? raw.data
            : [];

        const formattedOptions: Option[] =
          data
            .map(
              (
                item: any
              ) => {
                const icdCode =
                  item?.icd ??
                  item?.code ??
                  item?.ICD ??
                  item?.id;

                if (
                  icdCode ===
                    null ||
                  icdCode ===
                    undefined ||
                  String(
                    icdCode
                  ).trim() ===
                    ""
                ) {
                  return null;
                }

                const icdDescription =
                  String(
                    item?.description ??
                      item?.desc ??
                      item?.Description ??
                      ""
                  ).trim();

                return {
                  /*
                   * ICD remains a string.
                   * Do not convert to
                   * database numeric ID.
                   */
                  value:
                    String(
                      icdCode
                    ),

                  label:
                    icdDescription
                      ? `${icdCode} - ${icdDescription}`
                      : String(
                          icdCode
                        ),
                } as Option;
              }
            )
            .filter(
              (
                option:
                  | Option
                  | null
              ): option is Option =>
                option !==
                null
            )
            .slice(
              0,
              RESULT_LIMIT
            );

        /*
         * Prevent cache from growing
         * forever.
         */
        if (
          cache.size >
          100
        ) {
          cache.clear();
        }

        cache.set(
          cacheKey,
          formattedOptions
        );

        setOptions(
          formattedOptions
        );
      } catch (error) {
        /*
         * Ignore error from an
         * outdated request.
         */
        if (
          requestId !==
          requestSequence.current
        ) {
          return;
        }

        console.error(
          "Error loading ICD options:",
          error
        );

        setOptions([]);
      } finally {
        if (
          requestId ===
          requestSequence.current
        ) {
          setLoading(false);
        }
      }
    }, [
      inputValue,
      cache,
    ]);

    /*
     * Debounced search.
     */
    useEffect(() => {
      const debounceTimeout =
        window.setTimeout(
          () => {
            void loadOptions();
          },
          250
        );

      return () =>
        window.clearTimeout(
          debounceTimeout
        );
    }, [loadOptions]);

    const handleChange =
      useCallback(
        (
          selected: MultiValue<Option>
        ) => {
          const selectedArray =
            selected as Option[];

          setSelectedOptions(
            selectedArray
          );

          onChange(
            selectedArray
          );
        },
        [onChange]
      );

    const handleInputChange =
      useCallback(
        (
          value: string,
          actionMeta: InputActionMeta
        ): string => {
          /*
           * Only actual typing
           * updates search state.
           */
          if (
            actionMeta.action ===
            "input-change"
          ) {
            setInputValue(
              value
            );
          }

          return value;
        },
        []
      );

    /*
     * Clear externally.
     */
    useEffect(() => {
      if (clear) {
        setSelectedOptions(
          []
        );

        setOptions([]);

        setInputValue("");

        /*
         * Cancel previous search
         * logically.
         */
        requestSequence.current +=
          1;
      }
    }, [clear]);

    /*
     * Keep edit/default values
     * synchronized.
     */
    useEffect(() => {
      if (
        defaultValue &&
        JSON.stringify(
          defaultValue
        ) !==
          JSON.stringify(
            selectedOptions
          )
      ) {
        setSelectedOptions(
          defaultValue
        );
      }
    }, [
      defaultValue,
      selectedOptions,
    ]);

    const customStyles: StylesConfig<
      Option,
      true
    > = useMemo(
      () => ({
        control: (
          styles
        ) => ({
          ...styles,

          minHeight:
            "40px",

          fontSize:
            "14px",

          borderRadius:
            "8px",

          cursor:
            "text",
        }),

        valueContainer: (
          styles
        ) => ({
          ...styles,

          fontSize:
            "14px",
        }),

        input: (
          styles
        ) => ({
          ...styles,

          fontSize:
            "14px",
        }),

        placeholder: (
          styles
        ) => ({
          ...styles,

          fontSize:
            "14px",

          color:
            "#6B7280",
        }),

        multiValue: (
          styles
        ) => ({
          ...styles,

          backgroundColor:
            "#007BFF",

          color:
            "#FFFFFF",

          borderRadius:
            "5px",
        }),

        multiValueLabel: (
          styles
        ) => ({
          ...styles,

          color:
            "#FFFFFF",
        }),

        multiValueRemove: (
          styles
        ) => ({
          ...styles,

          color:
            "#FFFFFF",

          cursor:
            "pointer",

          ":hover": {
            backgroundColor:
              "#0056b3",

            color:
              "#FFFFFF",
          },
        }),

        option: (
          styles,
          {
            isSelected,
            isFocused,
          }
        ) => ({
          ...styles,

          backgroundColor:
            isSelected
              ? "#007BFF"
              : isFocused
              ? "#E6F0FA"
              : "#FFFFFF",

          color:
            isSelected
              ? "#FFFFFF"
              : "#000000",

          fontSize:
            "14px",

          padding:
            "8px 10px",

          /*
           * Important for mouse
           * interaction.
           */
          cursor:
            "pointer",

          pointerEvents:
            "auto",

          ":hover": {
            backgroundColor:
              "#0056b3",

            color:
              "#FFFFFF",
          },
        }),

        menu: (
          styles
        ) => ({
          ...styles,

          zIndex:
            999999,

          pointerEvents:
            "auto",
        }),

        menuPortal: (
          styles
        ) => ({
          ...styles,

          zIndex:
            999999,

          pointerEvents:
            "auto",
        }),

        menuList: (
          styles
        ) => ({
          ...styles,

          maxHeight:
            "150px",

          overflowY:
            "auto",

          pointerEvents:
            "auto",

          WebkitOverflowScrolling:
            "touch",
        }),
      }),
      []
    );

    /*
     * Important:
     *
     * Do not wrap react-select Menu
     * inside another <div>.
     *
     * Keep original innerProps and
     * add QA attributes directly.
     */
    const Menu = (
      props: MenuProps<
        Option,
        true
      >
    ) => {
      const qa =
        qaAttrs(
          qaIdPrefix,
          "menu"
        );

      return (
        <components.Menu
          {...props}
          innerProps={{
            ...(props.innerProps ||
              {}),

            ...qa,
          }}
        />
      );
    };

    /*
     * Explicit MenuList.
     *
     * This keeps scroll handling
     * inside react-select.
     */
    const MenuList = (
      props: MenuListProps<
        Option,
        true
      >
    ) => (
      <components.MenuList
        {...props}
      >
        {props.children}
      </components.MenuList>
    );

    /*
     * Important:
     *
     * Do not wrap the Option
     * inside another div.
     *
     * react-select keeps mouse
     * handlers inside innerProps.
     */
    const OptionComponent = (
      props: OptionProps<
        Option,
        true
      >
    ) => {
      const qa =
        qaAttrs(
          qaIdPrefix,
          `option-${qaSafe(
            String(
              props.data.value
            )
          )}`
        );

      return (
        <components.Option
          {...props}
          innerProps={{
            /*
             * Keep original
             * react-select handlers.
             */
            ...props.innerProps,

            /*
             * Then add QA attrs.
             */
            ...qa,

            "data-option-label":
              props.data.label,

            "data-option-value":
              props.data.value,
          } as any}
        />
      );
    };

    /*
     * Selected chips.
     *
     * Also no external wrapper.
     */
    const MultiValueComponent = (
      props: MultiValueProps<
        Option,
        true
      >
    ) => (
      <components.MultiValue
        {...props}
        innerProps={{
          ...(props.innerProps ||
            {}),

          ...qaAttrs(
            qaIdPrefix,
            `selected-${qaSafe(
              String(
                props.data.value
              )
            )}`
          ),

          "data-selected-label":
            props.data.label,

          "data-selected-value":
            props.data.value,
        } as any}
      />
    );

    return (
      <div
        {...qaAttrs(
          qaIdPrefix,
          "wrapper"
        )}
      >
        <Select<
          Option,
          true
        >
          isMulti

          /*
           * IDs for automation
           */
          inputId={
            inputQaId
          }

          instanceId={
            selectQaId
          }

          name={
            selectQaId
          }

          classNamePrefix={
            selectQaId
          }

          className={
            selectQaId
          }

          aria-label="ICD medical code selector"

          data-testid={
            selectQaId
          }

          data-qa={
            selectQaId
          }

          /*
           * Controlled selected
           * values.
           */
          value={
            selectedOptions
          }

          onChange={
            handleChange
          }

          /*
           * Search results.
           */
          options={
            options
          }

          isLoading={
            loading
          }

          onInputChange={
            handleInputChange
          }

          placeholder="Type at least 3 letters to search..."

          /*
           * Portal prevents clipping
           * by dialog/container.
           */
          menuPortalTarget={
            typeof document !==
            "undefined"
              ? document.body
              : undefined
          }

          menuPosition="fixed"

          menuPlacement="bottom"

          menuShouldScrollIntoView={
            false
          }

          /*
           * Important mouse /
           * scroll behavior.
           */
          closeMenuOnSelect={
            false
          }

          blurInputOnSelect={
            false
          }

          captureMenuScroll={
            true
          }

          /*
           * Menu height.
           */
          maxMenuHeight={
            150
          }

          /*
           * Styling.
           */
          styles={{
            ...customStyles,

            menu: (
              base: any
            ) => ({
              ...base,

              zIndex:
                999999,

              pointerEvents:
                "auto",
            }),

            menuPortal: (
              base: any
            ) => ({
              ...base,

              zIndex:
                999999,

              pointerEvents:
                "auto",
            }),

            menuList: (
              base: any
            ) => ({
              ...base,

              maxHeight:
                150,

              overflowY:
                "auto",

              pointerEvents:
                "auto",

              WebkitOverflowScrolling:
                "touch",
            }),
          }}

          noOptionsMessage={() =>
            loading
              ? "Loading..."
              : inputValue
                    .trim()
                    .length <
                  MIN_SEARCH_LENGTH
              ? "Type at least 3 letters..."
              : "No options found"
          }

          /*
           * Custom components.
           *
           * Important:
           * no extra wrappers around
           * Menu or Option.
           */
          components={{
            Menu,

            MenuList,

            Option:
              OptionComponent,

            MultiValue:
              MultiValueComponent,
          }}
        />

        <span
          {...qaAttrs(
            qaIdPrefix,
            "loading-state"
          )}
          data-loading={
            loading
              ? "true"
              : "false"
          }
          style={{
            display:
              "none",
          }}
        >
          {loading
            ? "Loading"
            : "Idle"}
        </span>

        <span
          {...qaAttrs(
            qaIdPrefix,
            "selected-count"
          )}
          data-count={
            selectedOptions.length
          }
          style={{
            display:
              "none",
          }}
        >
          {
            selectedOptions.length
          }
        </span>

        <span
          {...qaAttrs(
            qaIdPrefix,
            "options-count"
          )}
          data-count={
            options.length
          }
          style={{
            display:
              "none",
          }}
        >
          {options.length}
        </span>

        <span
          {...qaAttrs(
            qaIdPrefix,
            "input-value"
          )}
          data-value={
            inputValue
          }
          style={{
            display:
              "none",
          }}
        >
          {inputValue}
        </span>
      </div>
    );
  }
);

ListofIcdMedicalCode.displayName =
  "ListofIcdMedicalCode";

export default ListofIcdMedicalCode;